import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { FaWhatsapp, FaPlayCircle } from "react-icons/fa";
import {
  Building2,
  HardHat,
  Store,
  Truck,
  Type,
  Zap,
} from "lucide-react";
import videoSrc from "../../assets/videos/video.mp4";

const HERO_ICON_DRIFT = "motion-reduce:animate-none animate-hero-icon-drift";

/** Signage / install–focused labels for the orbit stations (desktop). */
const ORBIT_SERVICES = [
  { Icon: Building2, title: "ACP façades & building wraps", drift: "0s" },
  { Icon: Store, title: "Retail storefront & mall signage", drift: "-2s" },
  { Icon: Zap, title: "LED, glow boards & neon", drift: "-4s" },
  { Icon: Type, title: "Channel letters & 3D logos", drift: "-6s" },
  { Icon: Truck, title: "Fabrication & site delivery", drift: "-8s" },
  { Icon: HardHat, title: "Professional on-site installation", drift: "-10s" },
];

function DriftBadge({ Icon, className, driftDelay, title }) {
  return (
    <div
      className={[
        "pointer-events-none z-20 flex select-none rounded-2xl border border-brand-amber/55 bg-black/60 text-brand-amber shadow-[0_0_32px_-10px_rgba(245,158,11,0.6)] backdrop-blur-md transition-transform duration-300 ease-out",
        "motion-reduce:transform-none lg:group-hover/media:scale-110 lg:group-hover/media:border-brand-yellow/80 lg:group-hover/media:shadow-[0_0_48px_-8px_rgba(250,204,21,0.48)]",
        className,
      ].join(" ")}
      title={title}
    >
      <span
        className={`${HERO_ICON_DRIFT} inline-flex p-3 sm:p-3.5 lg:p-[0.95rem]`}
        style={{ animationDelay: driftDelay }}
      >
        <Icon
          className="h-6 w-6 lg:h-7 lg:w-7"
          strokeWidth={1.65}
          aria-hidden
        />
      </span>
    </div>
  );
}

function HeroOuterRing() {
  return (
    <div
      className="pointer-events-none absolute left-1/2 top-1/2 z-[1] h-[calc(100%+6rem)] w-[calc(100%+6rem)] -translate-x-1/2 -translate-y-1/2 motion-reduce:animate-none animate-hero-outer-ring"
      aria-hidden
    >
      <svg
        viewBox="0 0 100 100"
        className="h-full w-full overflow-visible drop-shadow-[0_0_14px_rgba(245,158,11,0.32)]"
      >
        <defs>
          <linearGradient id="heroOuterRingGrad" x1="0" y1="0" x2="100" y2="100">
            <stop offset="0%" stopColor="#EA580C" />
            <stop offset="45%" stopColor="#F59E0B" />
            <stop offset="100%" stopColor="#FACC15" />
          </linearGradient>
        </defs>
        <rect
          x="2.2"
          y="2.2"
          width="95.6"
          height="95.6"
          rx="11"
          ry="11"
          fill="none"
          stroke="url(#heroOuterRingGrad)"
          strokeWidth="1.05"
          vectorEffect="non-scaling-stroke"
          strokeDasharray="3.4 4.2"
          opacity="0.98"
        />
      </svg>
    </div>
  );
}

/** Fills the visual “gutter” between copy and media on wide screens. */
const MID_COLUMN_PILLS = [
  "ACP & cladding",
  "LED & video walls",
  "Neon & glow signs",
  "3D channel letters",
  "Site install team",
];

function HeroMidField({ visible }) {
  return (
    <div
      className="pointer-events-none absolute inset-0 z-[6] hidden lg:block"
      aria-hidden
    >
      <div className="absolute left-1/2 top-1/2 h-[min(76vh,680px)] w-[min(24rem,34vw)] xl:w-[min(26rem,32vw)] -translate-x-1/2 -translate-y-1/2 bg-[radial-gradient(ellipse_48%_72%_at_50%_50%,rgba(234,88,12,0.14)_0%,rgba(245,158,11,0.06)_38%,transparent_70%)]" />

      <div className="absolute left-1/2 top-1/2 flex h-[min(74vh,640px)] w-[10.25rem] xl:w-[12rem] -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-evenly py-[min(5vh,2.75rem)] lg:py-[min(6vh,3rem)]">
        <div className="absolute inset-y-[6%] left-1/2 w-px -translate-x-1/2 bg-gradient-to-b from-transparent via-brand-amber/50 to-transparent" />
        <div className="absolute inset-y-[6%] left-1/2 w-10 -translate-x-1/2 bg-gradient-to-b from-transparent via-brand-orange/[0.12] to-transparent blur-md" />

        {MID_COLUMN_PILLS.map((label, i) => (
          <div
            key={label}
            className="relative z-10 flex flex-col items-center"
          >
            <div
              className={[
                "motion-reduce:animate-none animate-hero-mid-drift",
                visible ? "opacity-100" : "opacity-0",
              ].join(" ")}
              style={{ animationDelay: `${i * 0.55}s` }}
            >
              <div className="relative">
                <div className="absolute -inset-2 rounded-full bg-gradient-to-br from-brand-orange/35 to-brand-amber/15 opacity-70 blur-lg motion-reduce:opacity-30" />
                <span className="relative block max-w-[10.5rem] xl:max-w-[11.5rem] text-center leading-snug rounded-2xl border border-brand-amber/55 bg-black/60 px-3 py-2 xl:px-3.5 xl:py-2.5 text-[9px] xl:text-[10px] font-bold uppercase tracking-[0.1em] xl:tracking-[0.12em] text-brand-amber/95 shadow-[0_0_28px_-10px_rgba(245,158,11,0.5)] backdrop-blur-md">
                  {label}
                </span>
              </div>
            </div>
            {i < MID_COLUMN_PILLS.length - 1 ? (
              <div className="flex flex-col items-center py-2">
                <span
                  className="motion-reduce:animate-none animate-hero-sparkle mb-1 inline-block h-2 w-2 rounded-full bg-gradient-to-br from-brand-yellow to-brand-amber shadow-[0_0_14px_3px_rgba(250,204,21,0.5)]"
                  style={{ animationDelay: `${i * 0.7}s` }}
                />
                <div className="h-8 w-px bg-gradient-to-b from-brand-amber/50 via-brand-amber/15 to-transparent" />
              </div>
            ) : null}
          </div>
        ))}
      </div>

      <div className="absolute left-[36%] top-[20%] h-10 w-10 rotate-45 rounded-lg border border-brand-amber/30 bg-brand-orange/[0.08] motion-reduce:animate-none animate-hero-icon-drift backdrop-blur-sm [animation-delay:-3s]" />
      <div className="absolute right-[36%] bottom-[24%] h-8 w-8 rotate-12 rounded-md border border-brand-yellow/25 bg-brand-amber/[0.07] motion-reduce:animate-none animate-hero-icon-drift backdrop-blur-sm [animation-delay:-7s]" />
    </div>
  );
}

/** Softer wash in the gutter for large tablets / small desktop (no spine overlap). */
function HeroGutterWash() {
  return (
    <div
      className="pointer-events-none absolute left-1/2 top-[36%] z-[4] hidden h-[min(560px,70vh)] w-[min(92vw,520px)] -translate-x-1/2 -translate-y-1/2 bg-[radial-gradient(ellipse_55%_60%_at_50%_50%,rgba(234,88,12,0.11)_0%,rgba(245,158,11,0.05)_42%,transparent_72%)] md:block lg:hidden"
      aria-hidden
    />
  );
}

function useHeroReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(id);
  }, []);
  return [ref, visible];
}

function Reveal({ children, delayMs = 0, visible, className = "" }) {
  return (
    <div
      className={[
        visible
          ? "animate-fade-up motion-reduce:animate-none motion-reduce:opacity-100"
          : "opacity-0",
        className,
      ].join(" ")}
      style={{ animationDelay: visible ? `${delayMs}ms` : undefined }}
    >
      {children}
    </div>
  );
}

function Hero() {
  const [heroRef, visible] = useHeroReveal();
  const mediaZoneRef = useRef(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const reduceMotionRef = useRef(false);

  useEffect(() => {
    reduceMotionRef.current = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
  }, []);

  const onMediaPointer = useCallback((e) => {
    if (reduceMotionRef.current) return;
    if (typeof window !== "undefined" && window.innerWidth < 1024) return;
    const el = mediaZoneRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const nx = (e.clientX - r.left) / r.width - 0.5;
    const ny = (e.clientY - r.top) / r.height - 0.5;
    setTilt({ x: nx * 22, y: ny * 22 });
  }, []);

  const onMediaLeave = useCallback(() => {
    setTilt({ x: 0, y: 0 });
  }, []);

  const videoClassName =
    "block h-auto w-auto max-h-[min(70vh,720px)] max-w-full object-contain object-center";

  return (
    <section
      ref={heroRef}
      className="relative min-h-[92vh] flex items-center overflow-hidden bg-surface text-white"
    >
      <div className="absolute inset-0 bg-gradient-hero-glow" aria-hidden />
      <div
        className="absolute inset-0 bg-[linear-gradient(180deg,rgba(10,10,10,0.3)_0%,#0a0a0a_100%)]"
        aria-hidden
      />

      <div
        className="absolute top-1/4 -right-24 w-[min(75vw,420px)] h-[min(75vw,420px)] rounded-full bg-gradient-to-bl from-brand-orange/25 via-brand-amber/10 to-transparent blur-3xl animate-glow-orbs"
        aria-hidden
      />
      <div
        className="absolute -bottom-28 -left-20 w-[min(85vw,480px)] h-[min(85vw,480px)] rounded-full bg-gradient-to-tr from-brand-yellow/14 via-transparent to-transparent blur-3xl animate-glow-orbs [animation-delay:1.4s]"
        aria-hidden
      />

      <div
        className="pointer-events-none absolute left-[52%] top-1/2 z-[3] hidden h-[min(90vh,820px)] w-[min(42vw,520px)] -translate-y-1/2 bg-[radial-gradient(ellipse_at_center,rgba(234,88,12,0.09)_0%,rgba(251,191,36,0.04)_38%,transparent_68%)] blur-2xl lg:block"
        aria-hidden
      />

      <div className="container mx-auto px-4 relative z-10 py-16 lg:py-20">
        <div className="relative">
          <HeroGutterWash />
          <HeroMidField visible={visible} />

          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-11 xl:gap-14 items-center">
          <div className="max-w-xl lg:max-w-2xl lg:pl-4 relative z-10">
            <Reveal visible={visible} delayMs={0}>
              <p className="text-brand-amber/90 text-xs md:text-sm uppercase tracking-[0.2em] font-semibold mb-4">
                Mumbai&apos;s trusted signage partner
              </p>
            </Reveal>

            <Reveal visible={visible} delayMs={90}>
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.08] mb-3">
                <span className="block text-white/95">
                  Let&apos;s Build Your
                </span>
              </h1>
            </Reveal>

            <Reveal visible={visible} delayMs={180}>
              <span className="block text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.08] text-gradient-brand drop-shadow-[0_0_40px_rgba(245,158,11,0.25)] mt-1 mb-4">
                Dream Brand
              </span>
              <div
                className={[
                  "h-1 w-28 md:w-36 rounded-full bg-gradient-brand origin-left scale-x-0 mb-5",
                  visible
                    ? "animate-hero-line motion-reduce:scale-x-100 motion-reduce:animate-none"
                    : "",
                ].join(" ")}
                style={{ animationDelay: visible ? "420ms" : undefined }}
                aria-hidden
              />
            </Reveal>

            <Reveal visible={visible} delayMs={280}>
              <p className="text-lg md:text-xl text-gray-400 font-medium mb-3">
                DEFINE ⋆ DESIGN ⋆ DISPLAY
              </p>
            </Reveal>

            <Reveal visible={visible} delayMs={360}>
              <div className="flex gap-4 mb-8 max-w-lg items-stretch">
                <div
                  className={[
                    "w-1 shrink-0 rounded-full bg-gradient-to-b from-brand-orange via-brand-amber to-brand-yellow origin-top scale-y-0",
                    visible
                      ? "motion-reduce:!scale-y-100 animate-grow-bar motion-reduce:animate-none"
                      : "",
                  ].join(" ")}
                  style={{ animationDelay: visible ? "450ms" : undefined }}
                  aria-hidden
                />
                <p className="text-gray-500 text-sm md:text-base leading-relaxed italic">
                  &ldquo;Premium ACP Facades, 3D Acrylic & Steel Letters, &
                  Dynamic Video Panels that make your brand a landmark.&rdquo;
                </p>
              </div>
            </Reveal>

            <Reveal visible={visible} delayMs={440}>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-6">
                <Link
                  to="/inquiry"
                  className="group inline-flex items-center justify-center gap-2 rounded-full bg-gradient-brand px-8 py-3.5 text-sm font-bold text-black shadow-brand-glow hover:opacity-95 transition-all hover:shadow-[0_0_36px_-4px_rgba(245,158,11,0.45)] active:scale-[0.98]"
                >
                  <FaPlayCircle
                    className="text-lg opacity-90 transition-transform duration-300 group-hover:scale-110"
                    aria-hidden
                  />
                  Inquiry Now
                </Link>
                <Link
                  to="/services"
                  className="inline-flex items-center justify-center rounded-full border-2 border-brand-amber/60 text-brand-amber px-8 py-3.5 text-sm font-semibold hover:bg-brand-amber/10 hover:border-brand-yellow transition-all duration-300 hover:-translate-y-0.5 active:scale-[0.98]"
                >
                  Browse services
                </Link>
              </div>
            </Reveal>

            <Reveal visible={visible} delayMs={520}>
              <a
                href={`https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER}`}
                target="_blank"
                rel="noopener noreferrer"
                className="group/wa inline-flex items-center gap-2 text-sm font-semibold text-brand-yellow hover:text-white transition-colors"
              >
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-gradient-brand text-black shadow-md transition-transform duration-300 group-hover/wa:scale-110 group-hover/wa:shadow-brand-glow">
                  <FaWhatsapp className="w-4 h-4" />
                </span>
                WhatsApp for instant quote
              </a>
            </Reveal>
          </div>

          <div
            ref={mediaZoneRef}
            onMouseMove={onMediaPointer}
            onMouseLeave={onMediaLeave}
            className={[
              "group/media relative z-10 w-fit max-w-full min-w-0 justify-self-center",
              visible
                ? "animate-scale-in motion-reduce:animate-none motion-reduce:opacity-100 motion-reduce:scale-100 opacity-0"
                : "opacity-0",
            ].join(" ")}
            style={{ animationDelay: visible ? "240ms" : undefined }}
          >
            {/* Mobile / tablet: simple frame, no orbit */}
            <div className="px-1 lg:hidden">
              <div className="overflow-hidden rounded-2xl border border-brand-amber/40 bg-surface-elevated/90 shadow-brand-glow">
                <video
                  src={videoSrc}
                  autoPlay
                  loop
                  muted
                  playsInline
                  className={videoClassName}
                />
              </div>
            </div>

            {/* Desktop: outer ring + orbit icons + subtle inner frame */}
            <div
              className="relative hidden lg:inline-block"
              style={
                {
                  "--orbit-r": "clamp(9.25rem, 19.5vw, 12.75rem)",
                }
              }
            >
              <div
                className="relative transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:!translate-x-0 motion-reduce:!translate-y-0 will-change-transform"
                style={{
                  transform: `translate3d(${tilt.x}px, ${tilt.y}px, 0)`,
                }}
              >
                <div
                  className="pointer-events-none absolute -inset-12 rounded-[2rem] bg-gradient-to-br from-brand-orange/22 via-transparent to-brand-amber/12 blur-3xl opacity-90"
                  aria-hidden
                />

                <HeroOuterRing />

                <div className="pointer-events-none absolute left-1/2 top-1/2 z-[8] h-0 w-0">
                  {ORBIT_SERVICES.map((item, i) => {
                    const deg = i * 60 - 90;
                    return (
                      <div
                        key={item.title}
                        className="absolute left-1/2 top-1/2 w-0 h-0"
                        style={{
                          transform: `translate(-50%, -50%) rotate(${deg}deg) translateY(calc(-1 * var(--orbit-r))) rotate(${-deg}deg)`,
                        }}
                      >
                        <div className="-translate-x-1/2 -translate-y-1/2">
                          <DriftBadge
                            Icon={item.Icon}
                            title={item.title}
                            driftDelay={item.drift}
                            className="relative"
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="relative z-10 overflow-hidden rounded-2xl bg-surface-elevated/95 shadow-[0_0_44px_-14px_rgba(234,88,12,0.38)] ring-1 ring-white/10 transition-shadow duration-300 group-hover/media:shadow-[0_0_58px_-10px_rgba(245,158,11,0.46)]">
                  <video
                    src={videoSrc}
                    autoPlay
                    loop
                    muted
                    playsInline
                    className={videoClassName}
                  />
                </div>
              </div>
            </div>
          </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Hero;
